import { isInternalUser } from '@steerfi/shared';
import {
  AlertCircle,
  AlertTriangle,
  Building2,
  Calendar,
  CheckCircle,
  CheckCircle2,
  CircleX,
  CreditCard,
  Landmark,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  User,
} from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getSupplierById,
  revealSupplierAccountNumber,
  type SupplierData,
  type SupplierDetailData,
  type SupplierEmail,
  setSupplierApproval,
  type UpdateSupplierPayload,
  updateSupplier,
} from '@/api/suppliers';
import { BeneficiaryForm, type BeneficiaryFormValues } from '@/components/beneficiary-form';
import {
  DetailInfoCell,
  DetailMetricCard,
  DetailSectionHeader,
} from '@/components/detail-page-primitives';
import { COMPLETED_STATUS_STYLES } from '@/components/status-chip';
import { SupplierNotesPanel } from '@/components/supplier-notes-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { formatAmount, formatDate, formatDateTime } from '@/lib/utils';
import {
  findMethodToReplace,
  findReturnedMethod,
  hasReturnedMethod,
  isMethodReturned,
  needsReissuableMethod,
} from '@/utils/returned-payment-method';
import {
  buildVerificationTooltipData,
  getLyonsACHTooltipErrorsByRow,
  getLyonsOFACTooltipErrors,
  getUSPSTooltipErrors,
  isAccountStatusInconclusive,
  isOwnershipInconclusive,
  resolveLyonsAchAccountStatusRowStatus,
  type VerificationStatusForTooltip,
  type VerificationTooltipData,
} from '@/utils/verification-tooltip';
import { getPaymentMethodDisplay, StatusChip, type SupplierStatus } from './status-chip';
import { UpdatePaymentMethodModal } from './update-payment-method-modal';

interface SupplierDetailContentProps {
  supplier: SupplierDetailData;
  labels: { singular: string; plural: string };
  externalRefLabel: string;
  onSupplierChange?: () => void;
}

function preferredPaymentMethodToCode(
  display: string | null | undefined
): 'ACH' | 'CHECK' | 'DIGITAL_WALLET' | '' {
  const normalized = display?.trim().toLowerCase() ?? '';
  if (!normalized) return '';
  if (
    normalized === 'ach' ||
    normalized === 'ach account' ||
    normalized === 'ach transfer' ||
    normalized === 'checking' ||
    normalized === 'savings'
  ) {
    return 'ACH';
  }
  if (normalized === 'check') return 'CHECK';
  if (
    normalized === 'digital_wallet' ||
    normalized === 'digital wallet' ||
    normalized === 'wallet' ||
    normalized.includes('virtual')
  ) {
    return 'DIGITAL_WALLET';
  }
  return '';
}

const getPrimaryItem = <T extends { is_primary?: boolean }>(items: T[] | undefined): T | null => {
  if (!items || items.length === 0) return null;
  return items.find((item) => item.is_primary) ?? items[0];
};

const formatAddress = (data: SupplierData, detailedData: SupplierDetailData | null): string => {
  const primary = getPrimaryItem(detailedData?.addresses);
  if (primary) {
    return [
      primary.address1,
      primary.address2,
      primary.city,
      primary.state,
      primary.postal_code,
      primary.country,
    ]
      .filter(Boolean)
      .join(', ');
  }
  return [data.street, data.city, data.state, data.zip_code, data.country]
    .filter(Boolean)
    .join(', ');
};

const paymentStatusDotClass = (status?: string | null): string => {
  const normalized = (status ?? '').toLowerCase();
  if (normalized.includes('completed')) return COMPLETED_STATUS_STYLES.dot;
  if (normalized.includes('released') || normalized.includes('paid')) return 'bg-green-500';
  if (normalized.includes('pending') || normalized.includes('approval')) return 'bg-amber-500';
  return 'bg-slate-400';
};

function approvalBadgeClass(approval: string | null): string | undefined {
  if (approval === 'APPROVED') {
    return 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950/50 dark:text-green-200';
  }
  return undefined;
}

function approvalLabel(approval: string | null): string {
  if (approval === 'APPROVED') return 'Approved';
  if (approval === 'DENIED') return 'Denied';
  // Anything else (null for a new supplier, or IN_PROGRESS after a payment
  // method is re-added) is the pre-decision state awaiting approve/deny.
  return 'Not reviewed';
}

function verificationSeverityClass(severity: VerificationTooltipData['severity']): string {
  if (severity === 'green') return 'text-green-700';
  if (severity === 'yellow') return 'text-amber-600';
  return 'text-red-600';
}

function VerificationRow({ label, tooltip }: { label: string; tooltip: VerificationTooltipData }) {
  const severityClass = verificationSeverityClass(tooltip.severity);
  const SeverityIcon =
    tooltip.severity === 'green'
      ? CheckCircle2
      : tooltip.severity === 'yellow'
        ? AlertTriangle
        : CircleX;

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border bg-background/60 px-3 py-2.5">
      <span className="text-sm">{label}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex cursor-default items-center gap-1.5 text-sm ${severityClass}`}
          >
            <SeverityIcon className="size-4" />
            {tooltip.label}
            {tooltip.badge && (
              <Badge
                variant="outline"
                className="ml-0.5 border-amber-400 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-700"
              >
                {tooltip.badge}
              </Badge>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-left">
          <div className="space-y-1.5">
            <p className={`text-xs font-semibold ${severityClass}`}>{tooltip.title}</p>
            <p className="whitespace-pre-line text-xs text-muted-foreground">{tooltip.body}</p>
            {tooltip.guidance && (
              <p className="text-xs italic text-muted-foreground">{tooltip.guidance}</p>
            )}
            {tooltip.lastVerified && (
              <p className="pt-1 text-[10px] text-muted-foreground/70">
                Last verified: {tooltip.lastVerified}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export const SupplierDetailContent: React.FC<SupplierDetailContentProps> = ({
  supplier,
  labels,
  externalRefLabel,
  onSupplierChange,
}) => {
  const { user } = useKeycloakAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [detailedData, setDetailedData] = useState<SupplierDetailData | null>(supplier);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [pendingApprovalAction, setPendingApprovalAction] = useState<'APPROVED' | 'DENIED' | null>(
    null
  );
  const [isEditing, setIsEditing] = useState(false);
  const [updateMethodOpen, setUpdateMethodOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);
  const [revealedAccountNumber, setRevealedAccountNumber] = useState<string | null>(null);

  const isInternal = isInternalUser(user?.roles);
  const canClientApproveOrReject = !isInternal;
  const data = detailedData ?? supplier;

  const primaryAddress =
    detailedData?.addresses?.find((address) => address.is_primary) ?? detailedData?.addresses?.[0];
  const primaryBank =
    detailedData?.bank_accounts?.find((account) => account.is_primary) ??
    detailedData?.bank_accounts?.[0];
  const primaryEmail = getPrimaryItem<SupplierEmail>(detailedData?.emails);
  const primaryPhone = getPrimaryItem(detailedData?.phones);
  const bankAccounts = detailedData?.bank_accounts ?? [];
  const paymentMethods = detailedData?.payment_methods ?? [];
  // The method that still needs review drives the banner + Update action.
  const returnedMethod = useMemo(() => findReturnedMethod(paymentMethods), [paymentMethods]);
  // The method the modal shows as "old method (for verification)".
  const methodToReplace = useMemo(() => findMethodToReplace(paymentMethods), [paymentMethods]);
  const paymentMethodNeedsReview =
    (detailedData?.payment_method_needs_review ?? false) && returnedMethod != null;
  // A Digital Wallet replacement leaves the return unresolved for reissue, so the
  // Update action stays available until an ACH or Check method is added.
  const showUpdateMethodAction = useMemo(
    () => returnedMethod != null || needsReissuableMethod(paymentMethods),
    [returnedMethod, paymentMethods]
  );
  // A return is "involved" once any method has a review state — keeps the
  // Payment Methods on File section from falling back to Banking.
  const showReturnedMethods = hasReturnedMethod(paymentMethods);
  const payments = detailedData?.payments ?? [];
  const hasPayments = payments.length > 0;
  const clientRelationships = useMemo(
    () =>
      isInternal && detailedData
        ? detailedData.relationships.filter((relationship) => relationship.direction === 'from')
        : [],
    [isInternal, detailedData]
  );
  const nonInternalEntityId = useMemo(
    () => (!isInternal ? (supplier.relationship_id ?? supplier.id ?? null) : null),
    [isInternal, supplier.relationship_id, supplier.id]
  );

  const hasIndividualName = Boolean(data.first_name || data.last_name);
  const isIndividual = data.type === 'INDIVIDUAL' || (!data.business_name && hasIndividualName);
  const personName = `${data.first_name || ''} ${data.last_name || ''}`.trim();
  const displayName = personName || data.business_name || 'Unknown';
  const formattedAddress = formatAddress(data, detailedData);
  const paymentMethodValue = data.preferred_payment_method
    ? getPaymentMethodDisplay(data.preferred_payment_method)
    : '-';
  const verificationValue = data.verification_status ?? '-';
  const externalRefValue = data.external_ref ?? '-';
  const relationshipApproval = data.relationship_approval ?? null;
  const isApproved = relationshipApproval === 'APPROVED';
  const isDenied = relationshipApproval === 'DENIED';
  const relationshipApprovalAt = data.relationship_approval_at ?? null;
  const normalizedPreferredPaymentMethod = (data.preferred_payment_method ?? '')
    .trim()
    .toLowerCase();
  const hideBankingSection =
    normalizedPreferredPaymentMethod === 'check' ||
    normalizedPreferredPaymentMethod === 'digital_wallet' ||
    normalizedPreferredPaymentMethod === 'digital wallet' ||
    normalizedPreferredPaymentMethod === 'digital_cash' ||
    normalizedPreferredPaymentMethod === 'digital cash';

  const verificationMethods = detailedData?.verification_methods ?? [];
  const achVerification = verificationMethods.find(
    (method) => method.code === 'LYONS_ACH_VERIFICATION'
  );
  const uspsVerification = verificationMethods.find(
    (method) => method.code === 'USPS_ADDRESS_VERIFICATION'
  );
  const ofacVerification = verificationMethods.find(
    (method) => method.code === 'LYONS_OFAC_SCREENING'
  );
  const achDetails =
    achVerification?.result_details != null ? achVerification.result_details : undefined;
  const { ownershipErrors: achOwnershipErrors, accountStatusErrors: achAccountStatusErrors } =
    getLyonsACHTooltipErrorsByRow(achDetails);
  const uspsErrors = getUSPSTooltipErrors(
    uspsVerification?.result_details != null ? uspsVerification.result_details : undefined
  );
  const ofacErrors = getLyonsOFACTooltipErrors(
    ofacVerification?.result_details != null ? ofacVerification.result_details : undefined
  );
  const achStatus = (achVerification?.status?.toUpperCase() ??
    'SKIPPED') as VerificationStatusForTooltip;
  const achLastVerified = achVerification?.verified_at
    ? formatDate(achVerification.verified_at)
    : null;
  const achAccountStatusRowStatus = (
    achDetails != null ? resolveLyonsAchAccountStatusRowStatus(achDetails, achStatus) : achStatus
  ) as VerificationStatusForTooltip;
  const verificationRows: Array<{ label: string; tooltip: VerificationTooltipData }> = [
    ...(!hideBankingSection
      ? [
          {
            label: 'Account Ownership',
            tooltip: buildVerificationTooltipData({
              status: achStatus,
              lastVerified: achLastVerified,
              errors: achOwnershipErrors,
              isInconclusive: isOwnershipInconclusive(achDetails),
              guidance: isOwnershipInconclusive(achDetails)
                ? 'Routing number exists but account ownership could not be confirmed. This does not necessarily indicate fraud; the account may be new or the bank may not participate in automated verification.'
                : undefined,
            }),
          },
          {
            label: 'Account Status',
            tooltip: buildVerificationTooltipData({
              status: achAccountStatusRowStatus,
              lastVerified: achLastVerified,
              errors: achAccountStatusErrors,
              isInconclusive: isAccountStatusInconclusive(achDetails),
              guidance: isAccountStatusInconclusive(achDetails)
                ? 'The account could not be verified at this time. The account may be too new or the financial institution may not support automated status checks.'
                : undefined,
            }),
          },
        ]
      : []),
    {
      label: 'USPS Address',
      tooltip: buildVerificationTooltipData({
        status: (uspsVerification?.status?.toUpperCase() ??
          'SKIPPED') as VerificationStatusForTooltip,
        lastVerified: uspsVerification?.verified_at
          ? formatDate(uspsVerification.verified_at)
          : null,
        errors: uspsErrors,
      }),
    },
    {
      label: 'OFAC',
      tooltip: buildVerificationTooltipData({
        status: (ofacVerification?.status?.toUpperCase() ??
          'SKIPPED') as VerificationStatusForTooltip,
        lastVerified: ofacVerification?.verified_at
          ? formatDate(ofacVerification.verified_at)
          : null,
        errors: ofacErrors,
      }),
    },
  ];

  const editInitialValues = useMemo((): Partial<BeneficiaryFormValues> | undefined => {
    if (!detailedData) return undefined;
    const paymentCode = preferredPaymentMethodToCode(detailedData.preferred_payment_method);
    return {
      caseId: detailedData.external_ref ?? '',
      firstName: detailedData.first_name ?? '',
      lastName: detailedData.last_name ?? '',
      email: detailedData.email ?? '',
      street: primaryAddress?.address1 ?? '',
      address2: primaryAddress?.address2 ?? '',
      city: primaryAddress?.city ?? '',
      state: primaryAddress?.state ?? '',
      zipCode: primaryAddress?.postal_code ?? '',
      addressVerificationStatus: 'not_checked',
      paymentMethod: paymentCode,
      bankRoutingNumber: primaryBank?.routing_number ?? '',
      bankAccountNumber: revealedAccountNumber ?? '',
      bankAccountType:
        primaryBank?.account_type?.toUpperCase() === 'SAVINGS' ? 'SAVINGS' : 'CHECKING',
    };
  }, [detailedData, primaryAddress, primaryBank, revealedAccountNumber]);

  const refreshSupplier = useCallback(
    async ({ showLoading = false }: { showLoading?: boolean } = {}) => {
      if (!supplier.id) return;
      if (showLoading) setIsLoading(true);
      setError(null);
      try {
        const next = await getSupplierById(supplier.id);
        setDetailedData(next);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : `Failed to load ${labels.singular.toLowerCase()} details`
        );
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [labels.singular, supplier.id]
  );

  useEffect(() => {
    setDetailedData(supplier);
    setIsEditing(false);
    setSaveError(null);
    setRevealedAccountNumber(null);
  }, [supplier]);

  useEffect(() => {
    const onNotesChanged = () => {
      void refreshSupplier();
    };
    window.addEventListener('steerfi:supplier-notes-changed', onNotesChanged);
    return () => window.removeEventListener('steerfi:supplier-notes-changed', onNotesChanged);
  }, [refreshSupplier]);

  const startEditing = useCallback(async () => {
    if (!data || hasPayments || revealLoading) return;
    setSaveError(null);
    const accountId = primaryBank?.id ?? null;
    if (accountId && data.id) {
      setRevealLoading(true);
      try {
        const { account_number } = await revealSupplierAccountNumber(data.id, accountId);
        setRevealedAccountNumber(account_number);
      } catch {
        toast.error(
          'Could not load the current account number. You can still update other fields, or type a new account number to replace it.'
        );
        setRevealedAccountNumber(null);
      } finally {
        setRevealLoading(false);
      }
    } else {
      setRevealedAccountNumber(null);
    }
    setIsEditing(true);
  }, [data, hasPayments, primaryBank?.id, revealLoading]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setSaveError(null);
    setRevealedAccountNumber(null);
  }, []);

  const handleMethodUpdated = useCallback(
    (updated: SupplierDetailData) => {
      setDetailedData(updated);
      onSupplierChange?.();
    },
    [onSupplierChange]
  );

  const handleSaveEdit = useCallback(
    async (values: BeneficiaryFormValues) => {
      const id = data?.id;
      if (!id) return;
      setSaveLoading(true);
      setSaveError(null);
      try {
        const payload: UpdateSupplierPayload = {
          first_name: values.firstName.trim() || null,
          last_name: values.lastName.trim() || null,
          email: values.email.trim() || null,
          phone_number: null,
          street: values.street.trim() || null,
          city: values.city.trim() || null,
          state: values.state || null,
          zip_code: values.zipCode.trim() || null,
          preferred_payment_method: values.paymentMethod || null,
        };
        if (values.paymentMethod === 'ACH') {
          payload.bank_account_type = values.bankAccountType;
          payload.bank_routing_number = values.bankRoutingNumber.trim() || null;
          const rawAccount = values.bankAccountNumber.trim();
          if (rawAccount && rawAccount !== revealedAccountNumber) {
            payload.bank_account_number = rawAccount;
          }
        }
        payload.address_verification_status = values.addressVerificationStatus;
        const initialExternalRef = (detailedData?.external_ref ?? '').trim();
        const newExternalRef = values.caseId?.trim() ?? '';
        if (newExternalRef !== initialExternalRef) {
          payload.external_ref = newExternalRef || null;
        }
        const updated = await updateSupplier(id, payload);
        setDetailedData(updated);
        onSupplierChange?.();
        setIsEditing(false);
        setRevealedAccountNumber(null);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      } finally {
        setSaveLoading(false);
      }
    },
    [data?.id, detailedData?.external_ref, onSupplierChange, revealedAccountNumber]
  );

  const runApprovalAction = useCallback(
    async (action: 'APPROVED' | 'DENIED') => {
      const id = data?.id;
      if (!id) return;
      try {
        await setSupplierApproval(id, action);
        onSupplierChange?.();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : `Failed to ${action === 'APPROVED' ? 'approve' : 'deny'}`
        );
      } finally {
        setApprovalLoading(false);
      }
    },
    [data?.id, onSupplierChange]
  );

  const handleApprove = useCallback(() => {
    if (!data?.id) return;
    if (approvalLoading) {
      setPendingApprovalAction('APPROVED');
      return;
    }
    setApprovalLoading(true);
    setPendingApprovalAction(null);
    void runApprovalAction('APPROVED');
  }, [approvalLoading, data?.id, runApprovalAction]);

  const handleDeny = useCallback(() => {
    if (!data?.id) return;
    if (approvalLoading) {
      setPendingApprovalAction('DENIED');
      return;
    }
    setApprovalLoading(true);
    setPendingApprovalAction(null);
    void runApprovalAction('DENIED');
  }, [approvalLoading, data?.id, runApprovalAction]);

  useEffect(() => {
    if (!approvalLoading && pendingApprovalAction && data?.id) {
      const next = pendingApprovalAction;
      setPendingApprovalAction(null);
      setApprovalLoading(true);
      void runApprovalAction(next);
    }
  }, [approvalLoading, data?.id, pendingApprovalAction, runApprovalAction]);

  if (isLoading && !detailedData) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <Skeleton key={item} className="h-28 w-full rounded-sm" />
          ))}
        </div>
        <Skeleton className="h-72 w-full rounded-sm" />
      </div>
    );
  }

  if (isEditing) {
    return (
      <Card className="gap-0 py-0">
        <DetailSectionHeader icon={Pencil} title={`Edit ${labels.singular}`} />
        <CardContent className="p-5">
          <BeneficiaryForm
            key={`edit-${data.id}`}
            mode="edit"
            externalRefLabel={externalRefLabel || 'External Reference'}
            initialValues={editInitialValues ?? {}}
            onSubmit={handleSaveEdit}
            onCancel={cancelEditing}
            submitLabel="Save"
            isSubmitting={saveLoading}
            error={saveError}
            partyId={data.id ?? null}
            accountId={primaryBank?.id ?? null}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={scrollContainerRef} className="space-y-6">
      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <Card className="gap-0 border-amber-200 bg-amber-50/40 py-0 dark:border-amber-500/30 dark:bg-amber-500/10">
        <DetailSectionHeader icon={ShieldCheck} title={`${labels.singular} Actions`} />
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <p className="max-w-sm text-sm text-muted-foreground">
              Review enrollment details, update editable information, and manage client approval.
            </p>
            <div className="flex flex-wrap gap-2 lg:shrink-0 lg:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => void startEditing()}
                disabled={saveLoading || hasPayments || revealLoading}
              >
                {revealLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Pencil className="mr-2 h-4 w-4" />
                )}
                Edit {labels.singular.toLowerCase()}
              </Button>
              {showUpdateMethodAction && canClientApproveOrReject && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100 dark:text-amber-300"
                  onClick={() => setUpdateMethodOpen(true)}
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  Update Payment Method
                </Button>
              )}
              <Button
                type="button"
                variant="destructive"
                disabled={!canClientApproveOrReject || approvalLoading || !data.id || isDenied}
                onClick={handleDeny}
              >
                <CircleX className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button
                type="button"
                disabled={!canClientApproveOrReject || approvalLoading || !data.id || isApproved}
                onClick={handleApprove}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                {approvalLoading ? 'Updating...' : 'Approve'}
              </Button>
            </div>
          </div>
          {hasPayments && (
            <p className="text-xs text-muted-foreground">
              Editing is disabled after payments are associated with this{' '}
              {labels.singular.toLowerCase()}.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DetailMetricCard
          icon={isIndividual ? User : Building2}
          label="Type"
          value={isIndividual ? 'Individual' : labels.singular}
          helper={data.client_name ?? 'No client on file'}
        />
        <DetailMetricCard
          icon={CreditCard}
          label="Payment Method"
          value={paymentMethodValue}
          helper={`${payments.length} payment${payments.length === 1 ? '' : 's'} associated`}
        />
        <DetailMetricCard
          icon={ShieldCheck}
          label="Verification"
          value={verificationValue}
          helper={`${verificationRows.length} verification check${verificationRows.length === 1 ? '' : 's'}`}
        />
        <DetailMetricCard
          icon={CheckCircle2}
          label="Approval"
          value={approvalLabel(relationshipApproval)}
          helper={
            relationshipApprovalAt
              ? formatDateTime(relationshipApprovalAt.toString())
              : 'Client review status'
          }
          tone={relationshipApproval === 'DENIED' ? 'destructive' : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="gap-0 py-0 lg:col-span-2">
          <DetailSectionHeader icon={Building2} title="Overview" />
          <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
            <DetailInfoCell label="Name" value={displayName} />
            <DetailInfoCell label={externalRefLabel || 'External Ref'} value={externalRefValue} />
            <DetailInfoCell
              label="Status"
              value={<StatusChip status={data.status as SupplierStatus} type="supplier" />}
            />
            <DetailInfoCell
              label="Relationship Approval"
              value={
                <Badge
                  variant={relationshipApproval === 'DENIED' ? 'destructive' : 'outline'}
                  className={approvalBadgeClass(relationshipApproval)}
                >
                  {relationshipApproval === 'APPROVED' && (
                    <CheckCircle2 className="mr-1 size-3.5" />
                  )}
                  {relationshipApproval === 'DENIED' && <CircleX className="mr-1 size-3.5" />}
                  {approvalLabel(relationshipApproval)}
                </Badge>
              }
            />
            <DetailInfoCell label="Created" value={formatDate(data.created_at.toString())} />
            <DetailInfoCell label="Updated" value={formatDate(data.updated_at.toString())} />
            {!isIndividual && (
              <>
                <DetailInfoCell
                  label="Registration Number"
                  value={detailedData?.registration_number ?? '-'}
                />
                <DetailInfoCell label="Tax ID" value={detailedData?.tax_id_masked ?? '-'} />
              </>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 py-0">
          <DetailSectionHeader icon={Mail} title="Contact" />
          <CardContent className="space-y-4 p-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm">
                  {primaryEmail?.email_address ?? data.email ?? 'No email on file'}
                </p>
                {primaryEmail?.is_verified && (
                  <p className="text-xs text-muted-foreground">Verified primary email</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="truncate text-sm">
                  {primaryPhone?.formatted_number ??
                    primaryPhone?.phone_number ??
                    data.phone_number ??
                    'No phone on file'}
                </p>
                {primaryPhone?.is_verified && (
                  <p className="text-xs text-muted-foreground">Verified primary phone</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {formattedAddress || 'No address on file'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {paymentMethodNeedsReview && returnedMethod && (
        <div className="flex items-start justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Payment method needs review
              </p>
              <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                {returnedMethod.return_code ? `ACH return ${returnedMethod.return_code} — ` : ''}
                {returnedMethod.return_reason ?? 'Returned by the bank.'}
                {returnedMethod.returned_at
                  ? ` Disbursement was returned by the bank on ${formatDate(returnedMethod.returned_at)}.`
                  : ''}
              </p>
            </div>
          </div>
          {canClientApproveOrReject && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0 rounded-lg border-amber-300 text-xs text-amber-800 hover:bg-amber-100 dark:text-amber-300"
              onClick={() => setUpdateMethodOpen(true)}
            >
              Update method
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {showReturnedMethods ? (
          <Card className="gap-0 py-0">
            <DetailSectionHeader
              icon={Landmark}
              title="Payment Methods on File"
              meta={
                canClientApproveOrReject ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-lg text-xs"
                    onClick={() => setUpdateMethodOpen(true)}
                  >
                    Add / Replace
                  </Button>
                ) : undefined
              }
            />
            <CardContent className="space-y-3 p-4">
              {paymentMethods.length > 0 ? (
                paymentMethods.map((method) => {
                  const isReturned = isMethodReturned(method);
                  return (
                    <div
                      key={method.id}
                      className={`flex items-center gap-3 rounded-lg border bg-background/60 p-3 ${
                        isReturned ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-500/10' : ''
                      }`}
                    >
                      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                        <Landmark className="size-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium">{method.label}</p>
                          <Badge variant="outline" className="text-[10px]">
                            {method.type}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {method.masked_detail ?? '—'}
                          {method.added_at ? ` · added ${formatDate(method.added_at)}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {isReturned ? (
                          <Badge variant="returned">Returned</Badge>
                        ) : method.is_preferred ? (
                          <Badge
                            variant="outline"
                            className="border-green-200 bg-green-50 text-green-800"
                          >
                            Preferred
                          </Badge>
                        ) : method.is_verified ? (
                          <Badge variant="outline">Verified</Badge>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">No payment methods on file</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="gap-0 py-0">
            <DetailSectionHeader
              icon={Landmark}
              title="Banking"
              meta={hideBankingSection ? 'Not required' : undefined}
            />
            <CardContent className="space-y-3 p-4">
              {hideBankingSection ? (
                <p className="text-sm text-muted-foreground">
                  Banking details are not required for the selected payment method.
                </p>
              ) : bankAccounts.length > 0 ? (
                bankAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="flex items-center gap-3 rounded-lg border bg-background/60 p-3"
                  >
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <Landmark className="size-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {account.bank_name || account.account_name || 'Bank Account'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {account.account_type?.toUpperCase() === 'SAVINGS' ? 'Savings' : 'Checking'}
                        {account.account_number_masked ? ` - ${account.account_number_masked}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {account.is_primary && <Badge variant="secondary">Primary</Badge>}
                      {account.is_verified && <Badge variant="outline">Verified</Badge>}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No bank accounts on file</p>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="gap-0 py-0">
          <DetailSectionHeader icon={ShieldCheck} title="Verification" />
          <CardContent className="space-y-2 p-4">
            {verificationRows.map((row) => (
              <VerificationRow key={row.label} label={row.label} tooltip={row.tooltip} />
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="gap-0 py-0">
          <DetailSectionHeader
            icon={Calendar}
            title="Payments"
            meta={`${payments.length} records`}
          />
          <CardContent className="p-4">
            {payments.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">No payments associated yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <Table caption={`Payments for ${displayName}`}>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="px-4 py-2.5 text-xs">Amount</TableHead>
                      <TableHead className="px-4 py-2.5 text-xs">Status</TableHead>
                      <TableHead className="px-4 py-2.5 text-right text-xs">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => {
                      const paymentStatus = payment.status?.name ?? payment.status?.code ?? '-';
                      return (
                        <TableRow key={payment.id}>
                          <TableCell className="px-4 py-2.5 text-sm font-medium">
                            {formatAmount(payment.amount_total, payment.currency?.code ?? 'USD')}
                          </TableCell>
                          <TableCell className="px-4 py-2.5 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                className={`inline-block size-1.5 rounded-full ${paymentStatusDotClass(paymentStatus)}`}
                              />
                              {paymentStatus}
                            </span>
                          </TableCell>
                          <TableCell className="px-4 py-2.5 text-right text-sm text-muted-foreground">
                            {formatDate(payment.created_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="gap-0 py-0">
          <DetailSectionHeader
            icon={Pencil}
            title="Notes"
            meta={(data.notes_count ?? 0) > 0 ? `${data.notes_count} notes` : undefined}
          />
          <CardContent className="p-4">
            <SupplierNotesPanel
              open
              nonInternalEntityId={nonInternalEntityId}
              isInternal={isInternal}
              clientRelationships={clientRelationships}
              clientsLoading={isLoading}
              supplierLabel={labels.singular.toLowerCase()}
              scrollContainerRef={scrollContainerRef}
            />
          </CardContent>
        </Card>
      </div>

      {data.id && (
        <UpdatePaymentMethodModal
          open={updateMethodOpen}
          onClose={() => setUpdateMethodOpen(false)}
          supplierId={data.id}
          supplierName={displayName}
          returnedMethod={methodToReplace}
          canApprove={canClientApproveOrReject}
          onSuccess={handleMethodUpdated}
        />
      )}
    </div>
  );
};
